import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { AssistMode, Proposal, SessionApi } from "../lib/api";
import { type Message, turnsToMessages } from "../lib/turns";
import { modelDisplayName } from "../lib/model";
import { AttachPicker } from "./AttachPicker";
import { renderStreamingMarkdown } from "./markdown";

/** The AI assistant.
 *
 * It answers, and it proposes. It never acts. Every edit and command arrives as
 * a card that sits there until the candidate decides, and the decision is a
 * request against a database row — this panel is a view of the gate, not the
 * gate itself, because a candidate has devtools.
 */



/** One message.
 *
 * Formatted the whole way through, including mid-stream. Rendering plain text
 * while streaming and switching to markdown at the end makes the answer visibly
 * reflow as it finishes, which reads as a glitch; balancing the unclosed
 * constructs lets one renderer run throughout.
 */
function Bubble({ message, streaming }: { message: Message; streaming: boolean }) {
  const html = useMemo(
    () => (message.role === "assistant" ? renderStreamingMarkdown(message.text) : ""),
    [message.role, message.text],
  );

  if (message.role !== "assistant") {
    return <div className={`bubble ${message.role}`}>{message.text}</div>;
  }
  if (!message.text) {
    return <div className="bubble assistant prose thinking">…</div>;
  }
  return (
    <div
      className={`bubble assistant prose${streaming ? " streaming" : ""}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// A generated file or a vendored library would crowd out the code that matters.
const MAX_ATTACH_BYTES = 200_000;
const MAX_ATTACH_FILES = 40;

interface Props {
  api: SessionApi;
  /** The file the candidate is looking at, attached so the model has context. */
  activePath: string | null;
  activeContents: string | null;
  onApplied: () => void;
}

export function AssistPanel({ api, activePath, activeContents, onApplied }: Props) {
  const [mode, setMode] = useState<AssistMode>("off");
  const [messages, setMessages] = useState<Message[]>([]);
  const [pending, setPending] = useState<Proposal[]>([]);
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  // Exactly what the assistant can read. It knows what exists, but it cannot
  // open anything itself, so this list is the whole of its view.
  const [attached, setAttached] = useState<string[]>([]);
  const [picking, setPicking] = useState(false);
  const [tokens, setTokens] = useState({ used: 0, total: 0 });
  const [model, setModel] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<string | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const tailRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api
      .assistState()
      .then((state) => {
        setMode(state.mode);
        setModel(state.model);
        setTokens({ used: state.tokens_used, total: state.tokens_total });
        setPending(state.pending);
        // The conversation is on the server. Without this a reload showed an
        // empty panel for a session that had one.
        setMessages(turnsToMessages(state.turns));
      })
      .catch(() => setMode("off"));
  }, [api]);

  useEffect(() => {
    tailRef.current?.scrollIntoView({ block: "end" });
  }, [messages, pending]);

  // The whole workspace by default. An interview project is a handful of files,
  // and making the candidate hand-pick context is friction that teaches us
  // nothing about them. The picker exists to narrow it, not to build it up.
  const chosenRef = useRef(false);
  const [allFiles, setAllFiles] = useState<string[]>([]);
  useEffect(() => {
    api
      .listFiles()
      .then((files) => {
        const paths = files
          .filter((f) => (f.size ?? 0) <= MAX_ATTACH_BYTES)
          .map((f) => f.path);
        setAllFiles(paths);
        if (!chosenRef.current) setAttached(paths.slice(0, MAX_ATTACH_FILES));
      })
      .catch(() => {
        // Falling back to the open file is better than sending nothing.
        if (!chosenRef.current && activePath) setAttached([activePath]);
      });
    // Deliberately once per session: re-running would undo the candidate's choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const send = useCallback(() => {
    const text = draft.trim();
    if (!text || streaming || mode === "off") return;

    setMessages((prev) => [...prev, { role: "you", text }]);
    setDraft("");
    setStreaming(true);

    const socket = new WebSocket(api.assistStreamUrl());
    socketRef.current = socket;
    let answer = "";

    socket.onopen = async () => {
      // Read each attached file at send time: the editor's copy may be stale,
      // and an unsaved buffer is what the candidate actually means.
      const attachments: Record<string, string> = {};
      for (const path of attached) {
        if (path === activePath && activeContents !== null) {
          attachments[path] = activeContents;
          continue;
        }
        try {
          attachments[path] = await api.read(path);
        } catch {
          // A file deleted since it was attached is simply not sent.
        }
      }
      socket.send(JSON.stringify({ type: "prompt", text, attachments }));
      setMessages((prev) => [...prev, { role: "assistant", text: "" }]);
    };

    socket.onmessage = (event) => {
      const message = JSON.parse(String(event.data));
      if (message.type === "text") {
        answer += message.delta;
        setMessages((prev) => {
          const next = [...prev];
          next[next.length - 1] = { role: "assistant", text: answer };
          return next;
        });
      } else if (message.type === "proposal") {
        setPending((prev) => [...prev, message as Proposal]);
      } else if (message.type === "done") {
        setTokens({ used: message.tokens_used, total: message.tokens_total });
        setStreaming(false);
        socket.close();
      } else if (message.type === "error") {
        setMessages((prev) => [...prev, { role: "error", text: message.message }]);
        setStreaming(false);
        socket.close();
      }
    };

    socket.onerror = () => {
      setStreaming(false);
      // The socket carries one prompt and is closed on `done`, so losing it
      // mid-answer is routine on a phone: backgrounding the tab or locking the
      // screen is enough. The answer itself is not lost -- it was recorded
      // server-side as it streamed -- so recover it rather than reporting a
      // failure for work that succeeded and was charged for.
      api
        .assistState()
        .then((state) => {
          setMessages(turnsToMessages(state.turns));
          setTokens({ used: state.tokens_used, total: state.tokens_total });
          setPending(state.pending);
        })
        .catch(() =>
          setMessages((prev) => [
            ...prev,
            { role: "error", text: "lost the connection to the assistant" },
          ]),
        );
    };
    socket.onclose = () => setStreaming(false);
  }, [api, draft, streaming, mode, attached, activePath, activeContents]);

  async function decide(proposal: Proposal, decision: "approve" | "reject") {
    setDeciding(proposal.action_id);
    try {
      const result = await api.decide(proposal.action_id, decision);
      setPending((prev) => prev.filter((p) => p.action_id !== proposal.action_id));
      if (decision === "approve") {
        const detail =
          proposal.tool === "run_command"
            ? `exit ${result.exit_code}\n${(result.stdout ?? "") + (result.stderr ?? "")}`.trim()
            : `applied to ${proposal.path}`;
        setMessages((prev) => [...prev, { role: "assistant", text: detail }]);
        onApplied();
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "error", text: err instanceof Error ? err.message : String(err) },
      ]);
    } finally {
      setDeciding(null);
    }
  }

  const exhausted = tokens.total > 0 && tokens.used >= tokens.total;

  return (
    <aside className="assist">
      <div className="pane-title">
        <span>AI assist</span>
        {tokens.total > 0 && (
          <span className="budget" title={`${tokens.used.toLocaleString()} of ${tokens.total.toLocaleString()} tokens`}>
            {Math.min(100, Math.round((tokens.used / tokens.total) * 100))}%
          </span>
        )}
      </div>

      {mode === "off" ? (
        <div className="assist-body">AI assist is off for this session.</div>
      ) : (
        <>
          <div className="assist-log">
            {messages.length === 0 && (
              <p className="assist-hint">
                Ask for code and it will answer. In this mode it can also propose
                edits and commands — nothing runs until you approve it.
              </p>
            )}
            {messages.map((message, i) => (
              <Bubble
                key={i}
                message={message}
                streaming={streaming && i === messages.length - 1}
              />
            ))}

            {pending.map((proposal) => (
              <div className="proposal" key={proposal.action_id}>
                <div className="proposal-head">
                  <span className="proposal-tool">
                    {proposal.tool === "apply_patch" ? "edit" : "run"}
                  </span>
                  <span>{proposal.summary}</span>
                </div>
                {proposal.cmd && <pre className="proposal-cmd">$ {proposal.cmd}</pre>}
                {proposal.path && (
                  <div className="proposal-file">
                    {proposal.path}{" "}
                    <span className="added">+{proposal.lines_added ?? 0}</span>
                    <span className="removed">−{proposal.lines_removed ?? 0}</span>
                  </div>
                )}
                {proposal.diff && (
                  <pre className="proposal-diff">
                    {proposal.diff.split("\n").slice(0, 400).map((line, i) => (
                      <span
                        key={i}
                        className={
                          line.startsWith("+") && !line.startsWith("+++")
                            ? "dl a"
                            : line.startsWith("-") && !line.startsWith("---")
                              ? "dl d"
                              : line.startsWith("@@")
                                ? "dl h"
                                : "dl"
                        }
                      >
                        {line || " "}
                      </span>
                    ))}
                  </pre>
                )}
                <div className="proposal-actions">
                  <button
                    className="button primary"
                    disabled={deciding === proposal.action_id}
                    onClick={() => decide(proposal, "approve")}
                  >
                    {proposal.tool === "apply_patch" ? "Apply" : "Run"}
                  </button>
                  <button
                    className="button"
                    disabled={deciding === proposal.action_id}
                    onClick={() => decide(proposal, "reject")}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
            <div ref={tailRef} />
          </div>

          {picking && (
            <AttachPicker
              api={api}
              attached={attached}
              onChange={(paths) => {
                chosenRef.current = true;
                setAttached(paths);
              }}
              onClose={() => setPicking(false)}
            />
          )}

          <div className="assist-compose">
            <div className="attached">
              {attached.length > 0 && attached.length === allFiles.length ? (
                <span className="chip-file whole" title={attached.join("\n")}>
                  whole workspace · {attached.length} files
                </span>
              ) : (
                attached.map((path) => (
                  <span className="chip-file" key={path}>
                    {path.split("/").pop()}
                    <button
                      onClick={() => {
                        chosenRef.current = true;
                        setAttached(attached.filter((p) => p !== path));
                      }}
                      title={`stop sending ${path}`}
                    >
                      ×
                    </button>
                  </span>
                ))
              )}
              {attached.length === 0 && <span className="assist-hint">nothing attached</span>}
              <button className="chip-add" onClick={() => setPicking((v) => !v)}>
                {attached.length === allFiles.length && allFiles.length > 0 ? "narrow" : "choose"}
              </button>
            </div>
            <textarea
              value={draft}
              placeholder={
                exhausted ? "token budget used up" : `Ask ${modelDisplayName(model)}…`
              }
              disabled={streaming || exhausted}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={3}
            />
            <button
              className="button primary"
              disabled={streaming || exhausted || !draft.trim()}
              onClick={send}
            >
              {streaming ? "…" : "Send"}
            </button>
          </div>
        </>
      )}
    </aside>
  );
}
